// react
import React, { useState } from 'react'
import papa from 'papaparse'
import { saveAs } from 'file-saver'

// components
import { Button, Input, Select } from '@st-operations/uikit/src/v2'
import Header from '_components/header'

// services
import { getPdpOmmBillableReport } from '_services/contextual-similarity-models-apa'
import { logErrorAsync } from '_lib/utility'

// contexts
import { usePDPEnvironment } from '../../contexts/PDPEnvContext'
import { DEFAULT_ENV } from '../predactiv-admin/helpers'

// styles
import './styles.scss'

const RANGE = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  ALL: 'all',
  CUSTOM: 'custom',
}

const RANGE_OPTIONS = [
  { label: 'Last day', value: RANGE.DAY },
  { label: 'Last 7 days', value: RANGE.WEEK },
  { label: 'Last 30 days', value: RANGE.MONTH },
  { label: 'All time', value: RANGE.ALL },
  { label: 'Custom range', value: RANGE.CUSTOM },
]

// The endpoint compares start/end against the pipeline's delivery date, which
// carries no time zone, so build the bounds as plain UTC calendar days.
const toUTCDate = (date) => date.toISOString().slice(0, 10)

const presetRange = (range) => {
  const start = new Date()
  const end = new Date()

  switch (range) {
    case RANGE.DAY:
      start.setUTCDate(start.getUTCDate() - 1)
      break
    case RANGE.WEEK:
      start.setUTCDate(start.getUTCDate() - 7)
      break
    case RANGE.MONTH:
      // A fixed 30 days, as the label says - not a calendar month, which is
      // 28 to 31 days depending on when it is picked.
      start.setUTCDate(start.getUTCDate() - 30)
      break
    default:
      // All time — both params omitted, which returns every billable campaign.
      return { start: '', end: '' }
  }

  return { start: toUTCDate(start), end: toUTCDate(end) }
}

const PdpOmmBillableReport = () => {
  // The provider only wraps the Predactiv Admin routes; elsewhere fall back to
  // the same default those pages start from.
  const { pdpEnvironment } = usePDPEnvironment() || {}
  const env = pdpEnvironment || DEFAULT_ENV

  // Default to everything: ops reconcile the whole history, not a window.
  const [range, setRange] = useState(RANGE.ALL)
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')
  const [downloading, setDownloading] = useState(false)

  const isCustom = range === RANGE.CUSTOM
  const { start, end } = isCustom ? { start: customStart, end: customEnd } : presetRange(range)

  const invalidCustomRange = isCustom && !!start && !!end && start > end
  const today = toUTCDate(new Date())

  const download = () => {
    setDownloading(true)

    getPdpOmmBillableReport({ env, start, end })
      .then((csv) => {
        const { data } = papa.parse(csv.trim(), { header: true, skipEmptyLines: true })
        const suffix = start || end ? `${start || 'start'}_${end || today}` : 'all-time'

        saveAs(
          new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
          `pdp-omm-billable-campaigns-${suffix}.csv`
        )

        if (data.length) {
          toast(`Downloaded ${data.length} campaign${data.length === 1 ? '' : 's'}.`)
        } else {
          toast('No billable OMM campaigns in that range — the file has headers only.')
        }
      })
      .catch((error) => {
        logErrorAsync(error)
        toast.error(error.message)
      })
      .finally(() => setDownloading(false))
  }

  // The sidebar already hides this page from client users; this covers a
  // direct URL.
  if (!window._isShareThisUser) {
    return (
      <div className="pdp-omm-billable-report">
        <Header title="PDP OMM Billable Report" />
        <div className="pdp-omm-billable-report__content">
          <p className="pdp-omm-billable-report__description">
            This report is available to ShareThis users only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="pdp-omm-billable-report">
      <Header title="PDP OMM Billable Report" />

      <div className="pdp-omm-billable-report__content">
        <p className="pdp-omm-billable-report__description">
          Every Online Media Measurement campaign PDP has delivered through StatSocial, across all
          organizations, one row per campaign — including campaigns deleted after delivery, marked
          by their model status. Filtered by delivery date.
        </p>

        <div className="pdp-omm-billable-report__controls">
          <Select
            className="pdp-omm-billable-report__range"
            label="Delivery Date Range"
            options={RANGE_OPTIONS}
            value={RANGE_OPTIONS.find((option) => option.value === range)}
            onChange={(option) => setRange(option.value)}
            isSearchable={false}
            useReactSelect
          />

          <If condition={isCustom}>
            <Input
              className="pdp-omm-billable-report__date"
              label="Start (UTC)"
              type="date"
              max={customEnd || today}
              value={customStart}
              onChange={setCustomStart}
            />
            <Input
              className="pdp-omm-billable-report__date"
              label="End (UTC)"
              type="date"
              min={customStart}
              value={customEnd}
              onChange={setCustomEnd}
            />
          </If>
        </div>

        <p className="pdp-omm-billable-report__summary">
          <Choose>
            <When condition={invalidCustomRange}>
              Start date must be on or before the end date.
            </When>
            <When condition={!start && !end}>Includes every billable campaign, all time.</When>
            <When condition={start && !end}>
              Includes campaigns delivered on or after {start} (UTC).
            </When>
            <When condition={!start && end}>
              Includes campaigns delivered on or before {end} (UTC).
            </When>
            <Otherwise>
              Includes campaigns delivered between {start} and {end} (UTC), inclusive.
            </Otherwise>
          </Choose>
        </p>

        <Button
          onClick={download}
          disabled={downloading || invalidCustomRange}
          isLoading={downloading}
        >
          Download CSV
        </Button>
      </div>
    </div>
  )
}

export default PdpOmmBillableReport
